<template>
  <ui-kit-select
    v-if="anchorsList.length"
    :value="anchorsList[currentAnchorIndex]"
    :items="anchorsList"
    :list-view="!isMobile"
    :label="isMobile ? '' : 'Table of contents'"
    :placeholder="isMobile ? 'Table of contents' : ''"
    class="blog-filter-item__anchor"
    @input="goToAnchor" />
</template>

<script>
import UiKitSelect from 'Components/ui/Select/UiKitSelect.vue';
import { _debounce as debounce, scrollIntoViewWithCallback } from 'Root/helpers/tools';

/**
 * @type {null|IntersectionObserver}
 */
let intersectionObserver = null;

export default {
  name: 'BlogFilterAnchors',

  components: {
    UiKitSelect,
  },

  props: {
    isMobile: {
      type: Boolean,
      default: true,
    },

    articleId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      anchorsList: [],
      currentAnchorIndex: -1,
      scrollTriggeredByClick: false,
      scrollDirection: 'down',
    };
  },

  watch: {
    isMobile(newValue) {
      if (newValue) {
        this.destroyIntersectionObserver();
      } else {
        this.setIntersectionObserver();
      }
    },

    articleId() {
      this.setAnchors();
      if (!this.isMobile) {
        this.setIntersectionObserver();
      }
    },
  },

  mounted() {
    this.setAnchors();
    if (!this.isMobile) {
      this.setIntersectionObserver();
    }
    window.addEventListener('scroll', this.handleScroll);
  },

  beforeDestroy() {
    this.destroyIntersectionObserver();
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    handleScroll() {
      this.scrollDirection = window.scrollY > this.lastScrollPosition ? 'down' : 'up';
      this.lastScrollPosition = window.scrollY;
    },

    setAnchors() {
      const anchorRefs = [];
      const contentNode = document.querySelector('.blog-article__content');
      [].slice.call(contentNode.querySelectorAll('h2,h3')).forEach((node, index) => {
        anchorRefs.push({
          id: index,
          node,
          title: node.innerText,
        });
      });

      this.anchorsList = anchorRefs;
    },

    setIntersectionObserver(rootNode) {
      const updateCurrentAnchorIndex = debounce((index) => {
        if (!this.scrollTriggeredByClick) {
          this.currentAnchorIndex = index;
        }
      }, 10);

      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = this.anchorsList.findIndex(
              (anchor) => anchor.node === entry.target,
            );

            if (entry.isIntersecting) {
              if (this.scrollDirection === 'down') {
                updateCurrentAnchorIndex(index);
              }
            } else if (this.scrollDirection === 'up') {
              const previousIndex = index > 0 ? index - 1 : 0;
              updateCurrentAnchorIndex(previousIndex);
            }
          });
        },
        {
          root: rootNode,
          threshold: 0,
          rootMargin: '0px 0px -90% 0px',
        },
      );

      this.anchorsList.forEach((anchor) => {
        intersectionObserver.observe(anchor.node);
      });
    },

    destroyIntersectionObserver() {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }
    },

    goToAnchor(anchor) {
      scrollIntoViewWithCallback(
        anchor.node,
        () => {
          this.scrollTriggeredByClick = true;
        },
        () => {
          setTimeout(() => {
            this.scrollTriggeredByClick = false;
          }, 300);
        },
      );
    },
  },
};
</script>

<style lang="scss" scoped>
  .blog-filter-item__anchor {
    --elements-color: #{$color--basic-light-gray};
    --bg-color: #{$color--basic-dark-gray};
    --head-color: #{$color--basic-light-gray};
    --head-color--open: #{$color--basic-light-gray};
  }

  @media screen and #{$screen-more-or-1280} {
    .blog-filter-item__anchor {
      --font-size: 15px;
      --border-radius: 15px;
      --font-size--with-label: 13px;
      --bg-color: #{$color--basic-menu};
      --elements-color: #{$color--basic-dark-gray};
    }
  }
</style>
